import React from 'react';
import MaterialSwitch from '@mui/material/Switch';
import {styled, alpha} from '@mui/material/styles';
import {excludeComponentProps} from '../utils/excludeComponentProps';

import type {SwitchProps as MaterialSwitchProps} from '@mui/material/Switch';

export interface SwitchProps extends Omit<MaterialSwitchProps, 'size' | 'color' | 'edge' | 'checkedIcon' | 'icon' | 'disableFocusRipple' | 'centerRipple' | 'disableRipple' |
'disableTouchRipple' | 'focusRipple' | 'TouchRippleProps' | 'readOnly'> {
    /**
     * Disable both icons
     */
    hideIcons?: boolean;
    /**
     * Disabled the 'On' Icon
     */
    hideOnIcon?: boolean;
    /**
     * Disabled the 'Off' Icon
     */
    hideOffIcon?: boolean;
}

const labelSpacing = 8;

const trackHeight = 20;
const trackWidth = 40;
const trackMarginX = 3;
const trackMarginY = trackMarginX;
const trackRadius = 100;

const iconSize = 14;

const thumbSize = trackHeight - trackMarginY * 2;

const getOnIcon = (dark?: boolean): string => `url("data:image/svg+xml,%3Csvg focusable='false' aria-hidden='true' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23${dark ? 'CCCCCC' : 'FFFFFF'}'%3E%3Cpath d='M0 0h24v24H0z' fill='none'%3E%3C/path%3E%3Cpath d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'%3E%3C/path%3E%3C/svg%3E")`;

const getOffIcon = (dark?: boolean): string => `url("data:image/svg+xml,%3Csvg focusable='false' aria-hidden='true' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23${dark ? 'CCCCCC' : 'FFFFFF'}'%3E%3Cpath d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'%3E%3C/path%3E%3Cpath d='M0 0h24v24H0z' fill='none'%3E%3C/path%3E%3C/svg%3E")`;

const StyledSwitch = styled(MaterialSwitch, {
    ...excludeComponentProps('hideOnIcon', 'hideOffIcon', 'hideIcons')
})<SwitchProps>(({
    theme, hideOnIcon, hideOffIcon, hideIcons
}) => {
    /** FIXME: Add Token!! Not future-proof, if color changes, or has more options */
    const disabledColor = theme.tokens.FuelColorBrandPrimary === '#367c2b' ?
        theme.tokens.FuelColorGreen200 :
        theme.tokens.FuelColorGray200;

    return {
        width: trackWidth,
        height: trackHeight,
        borderRadius: trackRadius,
        marginBlock: labelSpacing,
        marginInlineEnd: labelSpacing,
        marginInlineStart: labelSpacing + 3, // FIXME: Adapting to -11px spacing on FormControlLabel
        overflow: 'visible',
        padding: 0,

        '& .MuiSwitch-track': {
            width: 'inherit',
            height: 'inherit',
            borderRadius: trackRadius,
            position: 'relative',
            opacity: '1 !important',

            backgroundColor: theme.tokens.FuelColorGray600,

            '&:before, &:after': {
                opacity: 1,
                display: 'block',
                position: 'absolute',
                top: trackHeight / 2 - iconSize / 2,

                height: iconSize,
                width: iconSize,
                marginInline: (thumbSize - iconSize) / 2,
                content: '""',

                backgroundPosition: 'center',

                ...hideIcons && {
                    display: 'none'
                }
            },

            '&:before': {
                backgroundImage: getOnIcon(),
                left: trackMarginX,

                ...hideOnIcon && {
                    display: 'none'
                }
            },

            '&:after': {
                backgroundImage: getOffIcon(),
                right: trackMarginX,

                ...hideOffIcon && {
                    display: 'none'
                }
            }
        },
        '& .MuiSwitch-switchBase': {
            width: thumbSize,
            height: thumbSize,
            position: 'absolute',
            top: trackHeight / 2 - thumbSize / 2,
            left: trackMarginX,

            boxSizing: 'content-box',
            boxShadow: `0px 1px 2px ${alpha(theme.tokens.FuelColorBlack, 0.25)}`,
            padding: 0,

            backgroundColor: theme.tokens.FuelColorBackgroundWhite,

            '&.Mui-disabled': {
                backgroundColor: theme.tokens.FuelColorGray300,
                boxShadow: 'none',
                transform: 'none',

                '& + .MuiSwitch-track': {
                    backgroundColor: theme.tokens.FuelColorGray100,

                    '&:before': {
                        backgroundImage: getOnIcon(true)
                    },

                    '&:after': {
                        backgroundImage: getOffIcon(true)
                    }
                }
            },

            '&.Mui-checked': {
                left: trackWidth - thumbSize - trackMarginX,
                transform: 'none',

                '& + .MuiSwitch-track': {
                    backgroundColor: theme.tokens.FuelColorBrandPrimary
                },

                '&:hover': {
                    backgroundColor: theme.tokens.FuelColorBackgroundWhite
                },

                '&.Mui-disabled + .MuiSwitch-track': {
                    backgroundColor: disabledColor
                },

                '&.Mui-focusVisible:before': {
                    right: '-3px',
                    left: 'auto'
                }
            },

            '&.Mui-focusVisible': {
                transition: 'none'
            },

            '&.Mui-focusVisible:before': {
                width: '2.5rem',
                height: '1.25rem',
                content: '""',
                display: 'block',
                padding: '0px',
                position: 'absolute',
                boxShadow: '0 0 0 0.125rem #ffffff, 0 0 0 0.25rem #1b1b1b',
                borderRadius: '9999px',
                left: '-0.1875rem',
                top: '-0.1875rem'
            },

            '&:hover': {
                backgroundColor: theme.tokens.FuelColorBackgroundWhite
            }
        },
        '& .MuiSwitch-thumb': {
            display: 'none'
        },
        '& .MuiSwitch-input': {
            top: '50%',
            transform: 'translateY(-50%)',
            width: '400%',
            height: '200%'
        }
    };
});

export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(({
    hideIcons = false, hideOnIcon = false, hideOffIcon = false, ...props
}, ref) => (
    <StyledSwitch
        ref={ref}
        {...props}
        color='primary'
        disableFocusRipple={true}
        disableRipple={true}
        disableTouchRipple={true}
        hideIcons={hideIcons}
        hideOffIcon={hideOffIcon}
        hideOnIcon={hideOnIcon}
        size='medium'
        tabIndex={0}
    />
));

Switch.displayName = 'Switch';
